/**
 * Centralized formatters so currency / dates / percentages render
 * consistently across the app (and so we can localize in one place later).
 */

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

// Whole-dollar variant for forecast grids where pennies are noise.
// `Intl.NumberFormat`'s default rounding mode is `halfExpand`, which
// rounds away from zero — so 0.50 → 1 and -0.50 → -1, matching the
// "round 0.50+ up, otherwise down" rule operators expect.
const USD_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const USD_COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const PERCENT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const DATE_SHORT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const DATE_LONG = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

const TIME = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

export function formatCurrency(
  cents: number | null | undefined,
  opts?: { compact?: boolean; whole?: boolean },
) {
  if (cents == null) return '—';
  const dollars = cents / 100;
  if (opts?.compact) return USD_COMPACT.format(dollars);
  if (opts?.whole) return USD_WHOLE.format(dollars);
  return USD.format(dollars);
}

/**
 * Convert cents to a plain "12.34" string suitable for pre-filling an
 * editable input. Returns an empty string for null/undefined so the
 * placeholder shows through instead of `0.00`.
 */
export function centsToInputDollars(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toFixed(2);
}

/**
 * Parse a free-text dollar amount ("$1,234.50", "1234.5", "") into integer
 * cents. Empty / blank input → null so we don't overwrite an unset value
 * with $0.00 just because the operator cleared the field. Throws on a
 * malformed input so the caller can surface a validation toast.
 */
export function inputDollarsToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/^\$/, '').replace(/,/g, '');
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`“${value}” isn't a valid dollar amount.`);
  }
  return Math.round(parsed * 100);
}

/**
 * Unit prices can be quoted below a cent ($0.3455 each). These keep up to
 * four decimal places of a cent so a line total can multiply first and round
 * once, instead of rounding the unit price to the cent before multiplying.
 */
const UNIT_CENT_SCALE = 10_000;

const USD_UNIT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

export function formatUnitCurrency(cents: number | null | undefined) {
  if (cents == null) return '—';
  return USD_UNIT.format(cents / 100);
}

export function centsToInputUnitDollars(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toFixed(6).replace(/(\.\d\d\d*?)0+$/, '$1');
}

/** Like `inputDollarsToCents`, but keeps fractional cents (to 1/10,000¢). */
export function inputDollarsToUnitCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/^\$/, '').replace(/,/g, '');
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`“${value}” isn't a valid dollar amount.`);
  }
  return Math.round(parsed * 100 * UNIT_CENT_SCALE) / UNIT_CENT_SCALE;
}

export function formatPercent(value: number | null | undefined) {
  if (value == null) return '—';
  return PERCENT.format(value);
}

export function formatDate(
  value: string | Date | null | undefined,
  variant: 'short' | 'long' = 'short',
) {
  if (value == null) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return variant === 'long' ? DATE_LONG.format(d) : DATE_SHORT.format(d);
}

/**
 * Format a calendar date stored as `YYYY-MM-DD` (a Postgres `date`). Parsed
 * as local midnight — `new Date('2026-10-12')` would be UTC midnight and
 * render as the previous day west of Greenwich.
 */
export function formatDateOnly(value: string | null | undefined) {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return DATE_SHORT.format(new Date(y, m - 1, d));
}

/** Today's local date as `YYYY-MM-DD`, for `<input type="date">` defaults. */
export function todayIsoDate(): string {
  return toIsoDate(new Date());
}

/** Local calendar date (`YYYY-MM-DD`) of a timestamp, so an evening time isn't read as the next UTC day. */
export function localIsoDate(value: string): string {
  return toIsoDate(new Date(value));
}

/** `YYYY-MM-DD` plus `days` calendar days, in local time. */
export function addDaysIsoDate(value: string, days: number): string {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return toIsoDate(new Date(y, m - 1, d + days));
}

function toIsoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

const TIME_WITH_SECONDS = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

export function formatDateTime(
  value: string | Date | null | undefined,
  opts?: { seconds?: boolean },
) {
  if (value == null) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const time = opts?.seconds ? TIME_WITH_SECONDS.format(d) : TIME.format(d);
  return `${DATE_SHORT.format(d)}, ${time}`;
}

export function formatRelative(value: string | Date | null | undefined) {
  if (value == null) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diffMs = d.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

  const abs = Math.abs(diffMin);
  if (abs < 60) return rtf.format(diffMin, 'minute');
  if (abs < 1440) return rtf.format(Math.round(diffMin / 60), 'hour');
  if (abs < 1440 * 30) return rtf.format(Math.round(diffMin / 1440), 'day');
  if (abs < 1440 * 365) return rtf.format(Math.round(diffMin / (1440 * 30)), 'month');
  return rtf.format(Math.round(diffMin / (1440 * 365)), 'year');
}

export function formatDelta(value: number | null | undefined) {
  if (value == null) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${PERCENT.format(value)}`;
}

const PHONE_EXTENSION = /^(.*?)\s*(?:x|ext\.?|extension)\s*(\d+)\s*$/i;

/**
 * Render a US phone number as `XXX-XXX-XXXX`. Accepts whatever the operator
 * typed — bare digits, `(555) 123-4567`, `+1 555 123 4567`, `555.123.4567` —
 * and preserves a trailing extension (`x123`). Anything that isn't a
 * 10-digit US number (international, partial, fax prefixes, …) is returned
 * trimmed but otherwise untouched so we never mangle data we don't
 * understand. Empty / null input → '' so callers can pick their own
 * placeholder ('—' in tables, nothing in inputs).
 */
export function formatPhone(value: string | null | undefined): string {
  if (value == null) return '';
  const raw = value.trim();
  if (!raw) return '';

  const extMatch = raw.match(PHONE_EXTENSION);
  const main = extMatch ? extMatch[1] : raw;
  const ext = extMatch ? extMatch[2] : null;

  const digits = main.replace(/\D/g, '');
  let core: string | null = null;
  if (digits.length === 10) core = digits;
  else if (digits.length === 11 && digits.startsWith('1')) core = digits.slice(1);
  if (core == null) return raw;

  const formatted = `${core.slice(0, 3)}-${core.slice(3, 6)}-${core.slice(6)}`;
  return ext ? `${formatted} x${ext}` : formatted;
}
